import type { KernelStatus } from "@/kernel/Kernel";
import { outputText } from "@/kernel/outputs";
import type { Notebook, Task, TaskResult, TaskRun } from "@/kernel/runner";
import { cellById, src } from "@/kernel/runner";
import { Button } from "@/ui/Button";
import { CodeBlock, CopyButton } from "@/ui/CodeBlock";
import { StateCard } from "@/ui/states";
import { CodeEditor } from "./CodeEditor";
import { BootCard } from "./EnvPanel";
import { Markdown } from "./markdown";
import { TASK_LOOK } from "./Sidebar";

export function promptFor(nb: Notebook, task: Task): string {
  const idx = nb.cells.findIndex((c) => c.id === task.cell_id);
  for (let i = idx - 1; i >= 0; i--) {
    const c = nb.cells[i]!;
    if (c.cell_type === "markdown") return src(c);
    if (c.cell_type === "code") break;
  }
  return "";
}

export function TaskView({
  nb,
  task,
  total,
  story,
  result,
  run,
  running,
  status,
  code,
  onCode,
  onRun,
  onRestart,
  next,
  onNext,
}: {
  nb: Notebook;
  task: Task;
  total: number;
  story?: string;
  result?: TaskResult;
  run?: TaskRun;
  running: boolean;
  status: KernelStatus;
  code: string;
  onCode: (v: string) => void;
  onRun: () => void;
  onRestart: () => void;
  next?: Task;
  onNext: () => void;
}) {
  const st = result?.status ?? "idle";
  const look = TASK_LOOK[st]!;
  const checks = cellById(nb, task.checks_cell_id);
  const taskCell = cellById(nb, task.cell_id);
  const outputs = run ? [...run.taskOutputs, ...run.checkOutputs] : [...(taskCell?.outputs ?? []), ...(checks?.outputs ?? [])];
  const text = outputText(outputs);
  const images = outputs.flatMap((o) => (o.output_type === "display_data" && typeof o.data["image/png"] === "string" ? [o.data["image/png"] as string] : []));
  const failed = st === "failed" && !running;
  const outLabel = running ? "выполняется" : st === "passed" ? "все проверки прошли" : st === "failed" ? "проверки упали" : text ? "вывод" : "нет вывода";
  const booting = status.state === "booting" || status.state === "idle";

  return (
    <div className="flex min-h-0 flex-col gap-3.5 overflow-y-auto px-6 pb-10 pt-5" data-testid="task-view">
      <div className="flex flex-wrap items-center gap-3">
        <h1 className="m-0 font-mono text-[20px] font-bold tracking-[-.01em] text-ink">
          Задача {task.n} · {task.title}
        </h1>
        <span className="rounded-sm border px-2 py-[3px] font-mono text-[11px]" style={{ color: look.fg, borderColor: st === "idle" ? "var(--bd2)" : look.fg }} data-testid="task-status">
          {look.label}
        </span>
        <span className="ml-auto font-mono text-[11px] text-mut">
          {story ? `сюжет «${story}» · ` : ""}задача {task.n} из {total}
        </span>
      </div>

      <section className="rounded-md border border-bd bg-card px-4 py-3.5">
        <div className="mb-2 flex gap-2 font-mono text-[10.5px] text-ink2">
          <span className="rounded-sm border border-bd px-1.5 py-px">markdown</span>
          <span className="rounded-sm border border-bd px-1.5 py-px">условие</span>
        </div>
        <Markdown text={promptFor(nb, task) || "Условие задачи приходит из ноутбука-шаблона."} />
      </section>

      <section className="overflow-hidden rounded-md border bg-bg2" style={{ borderColor: running ? "var(--warn)" : "var(--bd)" }}>
        <div className="flex items-center gap-2.5 border-b border-bd bg-card px-3 py-2 font-mono text-[11px] text-ink2">
          <span>In [{task.n}]</span>
          <span>шаблон функции · python</span>
          <span className="flex-1" />
          <span className="hidden text-mut md:inline">Shift+Enter — запустить</span>
          <Button size="sm" onClick={onRun} disabled={running || status.state === "dead"} reason={status.state === "booting" ? "ядро загружается — запуск начнётся после" : undefined}>
            ▷ запустить
          </Button>
        </div>
        <CodeEditor key={task.cell_id} value={code} onChange={onCode} onRun={onRun} ariaLabel={`код задачи ${task.n}`} />
      </section>

      {checks && (
        <section className="overflow-hidden rounded-md border border-bd bg-bg2">
          <div className="flex items-center gap-2.5 border-b border-bd bg-card px-3 py-2 font-mono text-[11px] text-ink2">
            <span>In [{task.n + 1}]</span>
            <span>проверки · только чтение</span>
            <span className="flex-1" />
            <span className="text-mut">ячейка задаётся заданием</span>
            <CopyButton text={src(checks)} />
          </div>
          <CodeBlock code={src(checks)} flat />
        </section>
      )}

      {booting && !run ? (
        <BootCard status={status} />
      ) : status.state === "dead" ? (
        <StateCard
          code="ERR_RUNNER"
          where="ноутбук"
          tone="err"
          title="Ядро упало"
          body="Скорее всего кончилась память или связь с CDN оборвалась при загрузке. Код сохранён — перезапустите ядро и выполните ячейки заново."
          detail={status.error}
          primary={{ label: "перезапустить ядро", onClick: onRestart }}
        />
      ) : (
        <section className="overflow-hidden rounded-md border" style={{ borderColor: failed ? "var(--err)" : "var(--bd)" }} data-testid="out-card">
          <div className="flex items-center gap-2.5 border-b border-bd bg-card px-3 py-2 font-mono text-[11px] text-ink2">
            <span>Out</span>
            <span data-testid="out-label">{outLabel}</span>
            <span className="flex-1" />
            <span>{run ? `${(run.durationMs / 1000).toFixed(1)} s` : "—"}</span>
          </div>
          <pre
            className="m-0 max-h-[360px] overflow-auto whitespace-pre-wrap px-3.5 py-2.5 font-mono text-[12.5px] leading-[1.55]"
            style={{ background: failed ? "var(--errsoft)" : "var(--bg2)", color: failed ? "var(--err)" : "var(--ink)" }}
            data-testid="out-text"
          >
            {running
              ? "Выполняется… Не закрывай вкладку во время запуска — ядро живёт в ней."
              : text || (result?.error ? `Последний запуск: ${result.error}\nЗапустите ячейку, чтобы увидеть полный трейсбек.` : "Ячейка ещё не запускалась")}
          </pre>
          {images.map((png, i) => (
            <img key={i} src={`data:image/png;base64,${png}`} alt={`график ${i + 1}`} className="block max-w-full border-t border-bd bg-white" />
          ))}
        </section>
      )}

      <div className="mt-2 flex flex-wrap items-center gap-3 border-t border-bd pt-3.5">
        {next ? (
          <>
            <span className="font-mono text-[11px] text-mut">следующая незакрытая задача</span>
            <Button onClick={onNext}>
              Задача {next.n} · {next.title} →
            </Button>
          </>
        ) : (
          <span className="font-mono text-[11px] text-ink2">все задачи прошли проверки в браузере</span>
        )}
        <span className="ml-auto font-mono text-[11px] text-mut">ноутбук выполняется в браузере на CPU · GPU появится в следующей версии</span>
      </div>
    </div>
  );
}
